import React, { useState } from "react";
import { Table, Empty } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import _ from "lodash";
import InstitutionProfile from "../../../../components/common/InstitutionProfile";
import "../../../../components/common/universalLedgerPrint.css"

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
    const [day, month, year] = val.split("-");
    return dayjs(`${year}-${month}-${day}`).format("DD MMM YYYY");
  }
  const parsed = dayjs(val);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(val);
};

const UniversalLedgerTable = ({
  printMode       = false,
  filters = {},
  pagination: paginationProp,
  // ── external data props (for linked party tab) ────────
  externalList    = null,   // if provided, overrides redux list
  externalOpening = null,
  externalClosing = null,
}) => {
  const {
    list: reduxList,
    loading,
    openingBalance: reduxOpening,
    closingBalance: reduxClosing,
  } = useSelector((state) => state.universalLedger);

  // Use external data if provided (linked party tab), otherwise redux data
  const list           = externalList    !== null ? externalList    : reduxList;
  const openingBalance = externalOpening !== null ? externalOpening : reduxOpening;
  const closingBalance = externalClosing !== null ? externalClosing : reduxClosing;

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const debitTotal  = _.sumBy(list, (r) => Number(r.debit_amount)  || 0);
  const creditTotal = _.sumBy(list, (r) => Number(r.credit_amount) || 0);

  const tableData = [
    { _rowId: "opening", rowType: "opening" },
    ...list.map((row, i) => ({
      ...row,
      _rowId: row.id ? `row-${row.id}-${i}` : `row-${i}`,
      rowType: "data",
      seq: i + 1,
    })),
    { _rowId: "totals",  rowType: "totals"  },
    { _rowId: "closing", rowType: "closing" },
  ];

  const spanLabel = (label) => ({
    children: label,
    props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
  });
  const spanHide = { children: null, props: { colSpan: 0 } };

  const columns = [
    {
      title: "No.",
      key: "no",
      width: 55,
      render: (_, r) => (r.rowType === "data" ? r.seq : ""),
    },
    {
      title: "Date",
      dataIndex: "created_date",
      key: "date",
      width: 115,
      render: (val, r) => {
        if (r.rowType === "opening") return spanLabel("Opening Balance :");
        if (r.rowType === "totals")  return spanLabel("Grand Total :");
        if (r.rowType === "closing") return spanLabel("Closing Balance :");
        return formatDate(val);
      },
    },
    {
      title: "Particular",
      dataIndex: "particular",
      key: "particular",
      width: 280,
      render: (val, r) => {
        if (r.rowType !== "data") return spanHide;
        if (val && val.includes("<"))
          return <div dangerouslySetInnerHTML={{ __html: val }} />;
        return val || "";
      },
    },
    {
      title: "VCH No",
      dataIndex: "vch_no",
      key: "vch_no",
      width: 115,
      render: (val, r) => {
        if (r.rowType !== "data") return spanHide;
        if (r.vch_type === "Advance" && val)
          return `ADV-${String(val).slice(-6).toUpperCase()}`;
        return val || "";
      },
    },
    {
      title: "VCH Type",
      dataIndex: "vch_type",
      key: "vch_type",
      width: 130,
      render: (val, r) => (r.rowType !== "data" ? spanHide : val || ""),
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit",
      align: "right",
      width: 120,
      render: (val, r) => {
        if (r.rowType === "opening") return <strong>{fmt(openingBalance)}</strong>;
        if (r.rowType === "totals")  return <strong>{fmt(debitTotal)}</strong>;
        if (r.rowType === "closing") return <strong>{fmt(closingBalance)}</strong>;
        return fmt(val);
      },
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit",
      align: "right",
      width: 120,
      render: (val, r) => {
        if (r.rowType === "opening" || r.rowType === "closing") return "";
        if (r.rowType === "totals") return <strong>{fmt(creditTotal)}</strong>;
        return fmt(val);
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: 120,
      render: (val, r) => (r.rowType === "data" ? fmt(val) : ""),
    },
  ];

  // ── PRINT MODE: render plain HTML like Balance Sheet ──────────────
// if (printMode) {
//   return (
//     <div className="ul-print-wrapper">
//       {/* Logo + company name header (same as Balance Sheet) */}
//       <div className="ul-print-header">
//         {(filters?.fromDate || filters?.toDate) && (
//           <p className="ul-print-asof">
//             {filters?.fromDate ? `From ${formatDate(filters.fromDate)}` : ""}
//             {filters?.toDate   ? `  To ${formatDate(filters.toDate)}`    : ""}
//           </p>
//         )}
//       </div>

//       <table className="ul-print-table">
//         <thead>
//           <tr className="ul-print-heading">
//             <th style={{ width: 40 }}>No.</th>
//             <th style={{ width: 90 }}>Date</th>
//             <th>Particular</th>
//             <th style={{ width: 90 }}>VCH No</th>
//             <th style={{ width: 90 }}>VCH Type</th>
//             <th style={{ width: 90, textAlign: "right" }}>Debit</th>
//             <th style={{ width: 90, textAlign: "right" }}>Credit</th>
//             <th style={{ width: 90, textAlign: "right" }}>Balance</th>
//           </tr>
//         </thead>
//         <tbody>
//           {/* Opening */}
//           <tr className="ul-print-total">
//             <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
//               Opening Balance :
//             </td>
//             <td style={{ textAlign: "right" }}><strong>{fmt(openingBalance)}</strong></td>
//             <td></td>
//             <td></td>
//           </tr>

//           {/* Data rows */}
//           {list.map((row, i) => (
//             <tr key={row.id ? `row-${row.id}-${i}` : `row-${i}`}>
//               <td>{i + 1}</td>
//               <td>{formatDate(row.created_date)}</td>
//               <td>
//                 {row.particular && String(row.particular).includes("<") ? (
//                   <span dangerouslySetInnerHTML={{ __html: row.particular }} />
//                 ) : (
//                   row.particular || ""
//                 )}
//               </td>
//               <td>
//                 {row.vch_type === "Advance" && row.vch_no
//                   ? `ADV-${String(row.vch_no).slice(-6).toUpperCase()}`
//                   : row.vch_no || ""}
//               </td>
//               <td>{row.vch_type || ""}</td>
//               <td style={{ textAlign: "right" }}>{fmt(row.debit_amount)}</td>
//               <td style={{ textAlign: "right" }}>{fmt(row.credit_amount)}</td>
//               <td style={{ textAlign: "right" }}>{fmt(row.balance)}</td>
//             </tr>
//           ))}

//           {/* Totals */}
//           <tr className="ul-print-total">
//             <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
//               Grand Total :
//             </td>
//             <td style={{ textAlign: "right" }}><strong>{fmt(debitTotal)}</strong></td>
//             <td style={{ textAlign: "right" }}><strong>{fmt(creditTotal)}</strong></td>
//             <td></td>
//           </tr>

//           {/* Closing */}
//           <tr className="ul-print-total">
//             <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
//               Closing Balance :
//             </td>
//             <td style={{ textAlign: "right" }}><strong>{fmt(closingBalance)}</strong></td>
//             <td></td>
//             <td></td>
//           </tr>
//         </tbody>
//       </table>
//     </div>
//   );
// }
  if (printMode) {
  return (
    <table className="ledger-print-table">
      <tbody>
        {/* <tr className="ledger-print-heading">
          <td>No.</td>
          <td>Date</td>
          <td>Particular</td>
          <td>VCH No</td>
          <td>VCH Type</td>
          <td style={{ textAlign: "right" }}>Debit</td>
          <td style={{ textAlign: "right" }}>Credit</td>
          <td style={{ textAlign: "right" }}>Balance</td>
        </tr> */}
        <tr className="ledger-print-heading">
          <td style={{ width: "4%" }}>No.</td>
          <td style={{ width: "10%" }}>Date</td>
          <td style={{ width: "20%" }}>Particular</td>
          <td style={{ width: "18%" }}>VCH No</td>
          <td style={{ width: "10%" }}>VCH Type</td>
          <td style={{ width: "10%", textAlign: "right" }}>Debit</td>
          <td style={{ width: "10%", textAlign: "right" }}>Credit</td>
          <td style={{ width: "10%", textAlign: "right" }}>Balance</td>
        </tr>

        <tr className="ledger-print-total">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Opening Balance :
          </td>
          <td style={{ textAlign: "right" }}>
            {fmt(openingBalance)}
          </td>
          <td />
          <td />
        </tr>

        {list.map((row, index) => (
          <tr key={row.id || index}>
            <td>{index + 1}</td>

            <td>{formatDate(row.created_date)}</td>

            <td>
              {row.particular && String(row.particular).includes("<") ? (
                <span dangerouslySetInnerHTML={{ __html: row.particular }} />
              ) : (
                row.particular || ""
              )}
            </td>

            <td
              style={{
                whiteSpace: "nowrap",
                wordBreak: "keep-all",
              }}
            >
              {row.vch_type === "Advance" && row.vch_no
                ? `ADV-${String(row.vch_no).slice(-6).toUpperCase()}`
                : row.vch_no || ""}
            </td>

            <td>{row.vch_type || ""}</td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.debit_amount)}
            </td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.credit_amount)}
            </td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.balance)}
            </td>
          </tr>
        ))}

        <tr className="ledger-print-total">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Grand Total :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(debitTotal)}
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(creditTotal)}
          </td>

          <td />
        </tr>

        <tr className="ledger-print-total">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Closing Balance :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(closingBalance)}
          </td>

          <td />
          <td />
        </tr>
      </tbody>
    </table>
  );
}

  if (!loading && list.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          externalList !== null
            ? "No transactions found for this period"
            : "Select an account type and account, then click Get Report"
        }
        style={{ padding: "48px 0" }}
      />
    );
  }

  return (
    <Table
      rowKey="_rowId"
      columns={columns}
      dataSource={tableData}
      loading={externalList !== null ? false : loading}
      pagination={
        paginationProp === false
          ? false
          : {
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
            }
      }
      onChange={(pg) => setPagination(pg)}
      bordered
      size={printMode ? "small" : "middle"}
      scroll={printMode ? undefined : { x: "max-content" }}
      rowClassName={(r) => (r.rowType !== "data" ? "ant-table-summary" : "")}
    />
  );
};

export default UniversalLedgerTable;